import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Form } from "@/components/ui/form";
import { motion, AnimatePresence } from "framer-motion";
import { useOnboardingForm } from "./use-onboarding-form";
import { NameStep } from "./steps/NameStep";
import { FocusStep } from "./steps/FocusStep";
import { DiscoveryStep } from "./steps/DiscoveryStep";
import { CompletionStep } from "./steps/CompletionStep";
import { OnboardingShowcase } from "./OnboardingShowcase";
import { cn } from "@/lib/utils";
import { SparkleIcon, Shield, Star } from "lucide-react";

export function OnboardingSurvey() {
  const {
    form,
    step,
    isSubmitting,
    showOtherFocus,
    showOtherDiscovery,
    handleNext,
    prevStep,
    onSubmit,
    isCompleted,
  } = useOnboardingForm();

  // If submitting, show nothing to prevent flash
  if (isSubmitting) {
    return null;
  }

  return (
    <div className="relative flex w-full h-[100dvh] items-center justify-center overflow-hidden px-4 sm:px-6 bg-gradient-to-br from-gray-900 via-blue-900/20 to-gray-900">
      {/* Background decorations */}
      <div 
        className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-10 pointer-events-none" 
        aria-hidden="true"
      />
      <div 
        className="absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-blue-500/10 blur-3xl pointer-events-none motion-reduce:opacity-0" 
        aria-hidden="true"
      />
      <div 
        className="absolute top-0 left-0 h-[28rem] w-[28rem] rounded-full bg-purple-500/10 blur-3xl pointer-events-none motion-reduce:opacity-0" 
        aria-hidden="true"
      />

      {/* Centered two-panel card */}
      <div className="relative z-10 flex w-full max-w-sm sm:max-w-md lg:max-w-5xl xl:max-w-6xl flex-col lg:flex-row overflow-hidden rounded-xl lg:rounded-2xl shadow-xl lg:shadow-2xl">
        {/* Showcase – hidden on small */}
        <aside 
          className="hidden basis-3/5 flex-col justify-between bg-black/20 p-8 backdrop-blur-sm lg:flex border-r border-gray-800/40"
          aria-label="Onboarding showcase and features"
        >
          <OnboardingShowcase step={step} />
          
          {/* Trust indicators - matches Auth SocialProof position */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <Shield className="h-3 w-3 text-blue-400" />
              <span>Secure, bank-level encryption</span>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span>Trusted by investors</span>
            </div>
          </div>
        </aside>

        {/* Onboarding form panel */}
        <main 
          className="flex flex-1 items-center justify-center bg-black/10 p-4 sm:p-6"
          role="main"
          aria-label="Onboarding form"
        >
          <div className="w-full max-w-sm sm:max-w-md space-y-4 sm:space-y-6">
            {/* Header */}
            <div className="text-center space-y-3 sm:space-y-4">
              <div className="flex items-center justify-center gap-2 mb-4 sm:mb-6">
                <img src="/strykr-logo.jpeg" alt="Strykr Ai" className="h-6 w-6 sm:h-8 sm:w-8 rounded-full" />
                <h1 className="text-lg sm:text-xl font-bold text-white">Strykr Ai</h1>
              </div>
              
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight">
                Let's Personalize Your Experience
              </h1>
              <p className="text-blue-200/80 text-xs sm:text-sm">
                Tell us about your investment goals
              </p>
            </div>

            {/* Progress */}
            {!isCompleted && (
              <div className="space-y-2 sm:space-y-3">
                <Progress 
                  value={(step / 3) * 100} 
                  className="h-2 bg-gray-800/70 rounded-full overflow-hidden" 
                />
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <SparkleIcon className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-400" />
                    <span className="text-xs text-emerald-400 font-medium">Quick Setup</span>
                  </div>
                  <p className="text-xs text-gray-400">
                    Step {step} of 3
                  </p>
                </div>
              </div>
            )}

            {/* Form Content */}
            {isCompleted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-4 sm:p-6"
              >
                <CompletionStep />
              </motion.div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
                  <AnimatePresence mode="wait" initial={false}>
                    {step === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-4 sm:p-6"
                      >
                        <NameStep form={form} />
                      </motion.div>
                    )}

                    {step === 2 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-4 sm:p-6"
                      >
                        <FocusStep form={form} showOtherFocus={showOtherFocus} />
                      </motion.div>
                    )}

                    {step === 3 && (
                      <motion.div
                        key="step3"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-4 sm:p-6"
                      >
                        <DiscoveryStep form={form} showOtherDiscovery={showOtherDiscovery} />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Navigation */}
                  <div className="flex justify-between gap-3">
                    {step > 1 ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={prevStep}
                        className="border-gray-700 bg-gray-800/40 hover:bg-gray-700/40 text-gray-200 text-sm px-4 py-2 sm:px-6 sm:py-3"
                        disabled={isSubmitting}
                      >
                        Back
                      </Button>
                    ) : (
                      <div></div>
                    )}
                    
                    <Button
                      type={step === 3 ? "submit" : "button"}
                      onClick={step < 3 ? handleNext : undefined}
                      className="bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 shadow-lg shadow-emerald-700/20 text-white font-medium text-sm px-4 py-2 sm:px-6 sm:py-3"
                      disabled={isSubmitting}
                    >
                      {step === 3 ? (isSubmitting ? "Saving..." : "Complete Setup") : "Continue"}
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}